import React, { memo, useState } from "react";
import { LoadingState } from "components/Shared";
// The DATASOURCE-DRIVEN pager, the one every other paginated list in the app
// imports by this path. `Pagination` off the `components/Shared` barrel is a
// different, older, prop-driven component.
import Pagination from "components/Shared/Tcp/Pagination";
import Datasource from "lib/datasource";
import SlaTaskRow from "./SlaTaskRow";
import StatusFilterPills from "./StatusFilterPills";
import { SlaSectionType, SlaStatus, SlaTaskListItem } from "../types";

interface Props {
  title: string;
  tasks: SlaTaskListItem[];
  isLoading: boolean;
  totalCount: number;
  onTaskClick: (task: SlaTaskListItem) => void;
  defaultCollapsed?: boolean; // default false (expanded)
  selectedStatus?: SlaStatus | null;
  onStatusChange?: (status: SlaStatus | null) => void;
  sectionType?: SlaSectionType; // required when onStatusChange is provided
  /**
   * The list's Datasource (from `useSlaTaskList`). Given one, the section
   * renders the shared Pagination below the rows.
   *
   * THE LIST IS PAGED SERVER-SIDE, 20 per page. `totalCount` in the heading is
   * the whole result set, so without a pager a 27-task section showed 20 rows
   * under a "(27)" heading and the other 7 were unreachable. Pagination hides
   * itself when there is nothing to page through.
   */
  datasource?: Datasource;
}

// Chevron icons — pointing down when expanded, right when collapsed
const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// api-contract D8: local ephemeral state; both sections expanded by default.
const CollapsibleTaskSection = ({
  title,
  tasks,
  isLoading,
  totalCount,
  onTaskClick,
  defaultCollapsed = false,
  selectedStatus,
  onStatusChange,
  sectionType = "open",
  datasource,
}: Props) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <div className="bg-white rounded shadow-sm mb-4">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            className="flex items-center gap-2 text-left w-full"
            onClick={() => setCollapsed((v) => !v)}
            aria-expanded={!collapsed}
          >
            <span className="text-18 font-bold">
              {title}
              {totalCount > 0 && (
                <span className="ml-2 text-14 text-black-40 font-normal">({totalCount})</span>
              )}
            </span>
            <span className="ml-auto text-black-40">
              {collapsed ? <ChevronRight /> : <ChevronDown />}
            </span>
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Pills render at the top of the expanded-content region so they
              disappear when the section is collapsed (acceptance criterion). */}
          {onStatusChange && (
            <div className="px-5">
              <StatusFilterPills
                selected={selectedStatus ?? null}
                onChange={onStatusChange}
                sectionType={sectionType}
              />
            </div>
          )}

          {isLoading && <LoadingState />}

          {!isLoading && tasks.length === 0 && (
            <div className="py-10 text-center text-black-40 text-14">
              No tasks requiring attention for the selected filters.
            </div>
          )}

          {!isLoading && tasks.length > 0 && (
            <div>
              {tasks.map((task) => (
                <SlaTaskRow key={task.id} task={task} onClick={onTaskClick} />
              ))}
            </div>
          )}

          {/* Below the rows, and rendered even while a page is loading so the
              pager does not jump out of the layout on every page change. */}
          {!!datasource && tasks.length > 0 && (
            <div className="px-5 pb-4">
              <Pagination datasource={datasource} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default memo(CollapsibleTaskSection);
